import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Add } from "iconsax-react";
import { Button } from "@/shared/ui";
import type { TaskListItem } from "../../../types/api";
import GoalCard from "./GoalCard";

interface Props {
  allTasks: TaskListItem[];
  canManage: boolean;
  onCreateKpi: () => void;
  onKpiClick: (task: TaskListItem, projectId: number) => void;
  projectId: number;
}

export default function GoalsSection({ allTasks, canManage, onCreateKpi, onKpiClick, projectId }: Props) {
  const { t } = useTranslation("BoardPage");

  const kpiTasks = useMemo(() => allTasks.filter((task) => task.task_type === "kpi"), [allTasks]);

  const childTasksMap = useMemo(() => {
    const map = new Map<number, TaskListItem[]>();
    for (const kpi of kpiTasks) {
      map.set(
        kpi.id,
        allTasks.filter((task) => task.parent_task === kpi.id),
      );
    }
    return map;
  }, [kpiTasks, allTasks]);

  return (
    <div className="p-7 radius-3xl surface-base-fill">
      <div className="flex items-center justify-between mb-5">
        <h3 className="text-body-bold-lg content-base-primary">{t("overview.goals.title")}</h3>
        {canManage && (
          <Button type="button" variant="secondary" size="sm" className="radius-2xs! gap-0.5!" onClick={onCreateKpi}>
            <Add size={16} color="currentColor" />
            {t("overview.goals.addGoal")}
          </Button>
        )}
      </div>

      {kpiTasks.length === 0 ? (
        <p className="text-body-regular-md content-base-low">{t("overview.goals.empty")}</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {kpiTasks.map((kpi) => (
            <GoalCard
              key={kpi.id}
              kpiTask={kpi}
              childTasks={childTasksMap.get(kpi.id) ?? []}
              onClick={() => onKpiClick(kpi, projectId)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

